import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useRef } from "react";
import { STARLINK_OBSTRUCTION_NO_DATA, } from "../../dto/Starlink/StarlinkModels";
const decodeSnr = (snr) => {
    if (Array.isArray(snr))
        return Uint8Array.from(snr);
    const binary = atob(snr);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++)
        bytes[i] = binary.charCodeAt(i);
    return bytes;
};
// 0 (obstructed) -> red, 0.5 -> yellow, 1 (clear) -> green
const snrColor = (value) => {
    const t = Math.min(1, Math.max(0, value / 254));
    if (t < 0.5) {
        const k = t / 0.5;
        return [
            229,
            Math.round(57 + (206 - 57) * k),
            Math.round(53 + (70 - 53) * k),
        ];
    }
    const k = (t - 0.5) / 0.5;
    return [
        Math.round(247 + (76 - 247) * k),
        Math.round(206 + (175 - 206) * k),
        Math.round(70 + (80 - 70) * k),
    ];
};
export const StarlinkObstructionMap = ({ map, size = 320 }) => {
    const canvasRef = useRef(null);
    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas === null || canvas === void 0 ? void 0 : canvas.getContext("2d");
        const { numRows, numCols } = map;
        if (!canvas || !ctx || numRows <= 0 || numCols <= 0)
            return;
        const snr = decodeSnr(map.snr);
        const grid = document.createElement("canvas");
        grid.width = numCols;
        grid.height = numRows;
        const gridCtx = grid.getContext("2d");
        const image = gridCtx.createImageData(numCols, numRows);
        for (let i = 0; i < numRows * numCols && i < snr.length; i++) {
            if (snr[i] === STARLINK_OBSTRUCTION_NO_DATA)
                continue;
            const [r, g, b] = snrColor(snr[i]);
            image.data[i * 4] = r;
            image.data[i * 4 + 1] = g;
            image.data[i * 4 + 2] = b;
            image.data[i * 4 + 3] = 255;
        }
        gridCtx.putImageData(image, 0, 0);
        const dpr = window.devicePixelRatio || 1;
        canvas.width = size * dpr;
        canvas.height = size * dpr;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        const c = size / 2;
        const radius = c - 1;
        ctx.clearRect(0, 0, size, size);
        // sky disc background (no data)
        ctx.save();
        ctx.beginPath();
        ctx.arc(c, c, radius, 0, Math.PI * 2);
        ctx.fillStyle = "#f1ecd9";
        ctx.fill();
        ctx.clip();
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(grid, 0, 0, size, size);
        ctx.restore();
        // guides: horizon edge, crosshair, minimum elevation ring
        ctx.strokeStyle = "rgba(92, 81, 56, 0.6)";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(c, c, radius, 0, Math.PI * 2);
        ctx.moveTo(c, 0);
        ctx.lineTo(c, size);
        ctx.moveTo(0, c);
        ctx.lineTo(size, c);
        ctx.stroke();
        if (map.maxThetaDeg > 0) {
            const minElevationRadius = ((90 - map.minElevationDeg) / map.maxThetaDeg) * radius;
            if (minElevationRadius > 0 && minElevationRadius < radius) {
                ctx.setLineDash([4, 4]);
                ctx.beginPath();
                ctx.arc(c, c, minElevationRadius, 0, Math.PI * 2);
                ctx.stroke();
                ctx.setLineDash([]);
            }
        }
        // compass
        const inset = 14;
        const directions = [
            ["N", c, inset],
            ["E", size - inset, c],
            ["S", c, size - inset],
            ["W", inset, c],
        ];
        ctx.font = "bold 12px sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        directions.forEach(([label, x, y]) => {
            ctx.beginPath();
            ctx.arc(x, y, 9, 0, Math.PI * 2);
            ctx.fillStyle = "rgba(255, 255, 255, 0.85)";
            ctx.fill();
            ctx.fillStyle = label === "N" ? "#e53935" : "#5c5138";
            ctx.fillText(label, x, y + 0.5);
        });
    }, [map, size]);
    return (_jsx("canvas", { ref: canvasRef, style: { width: size, height: size, maxWidth: "100%" } }));
};
